'use client'

import { useState } from 'react'
import Link from 'next/link'
import posthog from 'posthog-js'
import { INDUSTRY_LINKS } from '../lib/site'

/* =========================================
   BOOK A DEMO FORM
   Bottom-of-page conversion form. Every "Book a Demo" CTA on
   the site scrolls to this section (#book-demo). Submissions POST
   to /api/demo, which emails the team via Resend.
   ========================================= */

const ORG_TYPES = [
  ...INDUSTRY_LINKS.map((link) => link.label),
  'Other',
]

/* The form card on its own, reusable outside the pinned section
   (the contact page swaps it in beside its channel list).
   reveal={false} skips the cascade-reveal class: an element mounted
   after page load is never observed, so it would stay at opacity 0. */
export function DemoFormCard({ reveal = true }: { reveal?: boolean }) {
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (submitting) return

    setSubmitting(true)
    setError(null)

    const formData = new FormData(e.currentTarget)
    const payload = Object.fromEntries(formData.entries())

    try {
      const res = await fetch('/api/demo', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        const data = await res.json().catch(() => null)
        throw new Error(data?.error || 'Something went wrong. Please try again.')
      }

      /* Tie this session (and its replay) to the real person: the
         email becomes the distinct ID, so the anonymous browsing
         history that led here merges into one identified profile. */
      const email = String(payload.email ?? '')
      if (email) {
        posthog.identify(email, {
          email,
          name: `${payload.firstName ?? ''} ${payload.lastName ?? ''}`.trim(),
          company: payload.company,
          org_type: payload.orgType,
        })
      }

      /* Attach the person to a company group so several people from the
         same firm read as one account rather than unrelated visitors.
         The project already defines a 'company' group type. */
      const company = String(payload.company ?? '').trim()
      if (company) {
        posthog.group('company', company.toLowerCase(), {
          name: company,
          org_type: payload.orgType,
          employees: payload.employees,
        })
      }

      posthog.capture('demo_request_submitted')
      setSubmitted(true)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Something went wrong. Please try again.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <>
        {/* The form never unmounts: on success the fields fade out in
            place (keeping the card's height, so the page doesn't jump)
            and the success message crossfades in on top.

            NOTE: the done state is a data attribute, not a class. The
            cascade-reveal observer adds `visible` to the DOM outside of
            React — if className were dynamic, React would rewrite it on
            re-render and strip `visible`, hiding the whole card. */}
        <form
          className={reveal ? 'demo-form-card reveal' : 'demo-form-card'}
          data-done={submitted ? '' : undefined}
          onSubmit={handleSubmit}
        >
          <fieldset className="demo-form-fields" disabled={submitted}>
            <div className="demo-form-row">
              <div className="demo-form-field">
                <input
                  type="text"
                  name="firstName"
                  className="demo-form-input"
                  placeholder="First Name*"
                  aria-label="First Name"
                  required
                />
              </div>
              <div className="demo-form-field">
                <input
                  type="text"
                  name="lastName"
                  className="demo-form-input"
                  placeholder="Last Name*"
                  aria-label="Last Name"
                  required
                />
              </div>
            </div>

            <div className="demo-form-field">
              <input
                type="email"
                name="email"
                className="demo-form-input"
                placeholder="Email*"
                aria-label="Email"
                required
              />
            </div>

            <div className="demo-form-field">
              <input
                type="text"
                name="company"
                className="demo-form-input"
                placeholder="Company Name*"
                aria-label="Company Name"
                required
              />
            </div>

            <div className="demo-form-field">
              <select
                name="orgType"
                className="demo-form-input demo-form-select"
                aria-label="Organisation type"
                defaultValue=""
                required
              >
                <option value="" disabled>
                  Organisation type*
                </option>
                {ORG_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div className="demo-form-field">
              <input
                type="text"
                name="employees"
                className="demo-form-input"
                placeholder="Number of employees*"
                aria-label="Number of employees"
                required
              />
            </div>

            <div className="demo-form-field">
              <input
                type="text"
                name="referralSource"
                className="demo-form-input"
                placeholder="How did you hear about us?"
                aria-label="How did you hear about us?"
              />
            </div>

            <p className="demo-form-legal">
              By submitting this form, you agree to the processing of your
              personal data in accordance with our{' '}
              <Link href="/privacy-policy" className="demo-form-legal-link">
                Privacy Policy
              </Link>
              .
            </p>

            {error && (
              <p className="demo-form-error" role="alert">
                {error}
              </p>
            )}

            <div className="demo-form-actions">
              <button
                type="submit"
                className="btn-primary btn-primary-lg"
                disabled={submitting}
              >
                {submitting ? (
                  <>
                    <span className="btn-spinner" aria-hidden="true" />
                    Sending…
                  </>
                ) : (
                  'Book a demo'
                )}
              </button>
            </div>
          </fieldset>

          {submitted && (
            <div className="demo-form-success" role="status">
              <svg
                className="demo-form-success-check"
                viewBox="0 0 52 52"
                aria-hidden="true"
              >
                <circle cx="26" cy="26" r="24" fill="none" />
                <path d="M15 27l8 8 15-16" fill="none" />
              </svg>
              <p className="demo-form-success-title">Request submitted</p>
              <p className="demo-form-success-body">
                Our team will reach back to you in 24 hours.
              </p>
            </div>
          )}
        </form>
    </>
  )
}

export function BookDemoForm() {
  return (
    /* The section is a tall runway with a sticky frame: the form stays
       pinned for a couple of viewport-heights of scroll before the
       page releases to the footer. Un-pins on mobile via CSS. */
    <section className="demo-form-section demo-form-pin" id="book-demo">
      <div className="demo-form-frame">
        <div className="demo-form-inner">
          <div className="demo-form-intro">
            <p className="section-label reveal">BOOK A DEMO</p>
            <h2 className="demo-form-heading reveal">
              See how Payna runs licensing.
            </h2>
            <p className="demo-form-subhead reveal">
              Tell us a little about the company and we&rsquo;ll walk through
              how Payna handles licensing and compliance in every US state.
            </p>
          </div>
          <DemoFormCard />
        </div>
      </div>
    </section>
  )
}
